import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { countParamsForUrl } from '../../util/unifiedSearch';

import { useSpaceTypeGroups } from './useSearchState';

/**
 * Live result counts for the editor ("57 Angebote anzeigen"), the city suggestions and the space
 * type options (spec §4.4).
 *
 * - Client only: the fetch lives in an effect, so the server render and the first client render
 *   never carry a number. Nothing runs while `enabled` is false.
 * - One `listings.query` per target URL with `perPage: 1`, reading `meta.totalItems`; the params
 *   come from `countParamsForUrl`, which mirrors exactly what the target page will query. When it
 *   returns null (a dates or seats filter) there is no exact number and the count stays unknown.
 * - Debounced 250 ms, so typing through a draft does not fire a request per keystroke.
 * - Module-level cache keyed by URL (60 s fresh) plus in-flight de-duplication: the target of the
 *   draft is usually one of the four space-type URLs, and reopening the editor shows the numbers
 *   at once. Responses are stored by URL, so a slow answer for an old draft can never be shown as
 *   the number of a newer one.
 */

const CACHE_TTL_MS = 60 * 1000;
export const COUNT_DEBOUNCE_MS = 250;

const cache = new Map(); // url → { count, at }
const inflight = new Map(); // url → Promise<number>

/** Test seam — the cache is module state that would otherwise leak between cases. */
export const clearListingCountCache = () => {
  cache.clear();
  inflight.clear();
};

const isFresh = url => {
  const entry = cache.get(url);
  return !!entry && Date.now() - entry.at < CACHE_TTL_MS;
};

// Display value: an expired entry is still better than no number while its refetch runs.
const cachedCount = url => cache.get(url)?.count;

const countThunk = params => (dispatch, getState, sdk) =>
  sdk.listings.query(params).then(response => response?.data?.meta?.totalItems);

const fetchCount = (dispatch, url, params) => {
  if (isFresh(url)) {
    return Promise.resolve(cachedCount(url));
  }
  if (inflight.has(url)) {
    return inflight.get(url);
  }
  const request = new Promise(resolve => resolve(dispatch(countThunk(params)))).then(
    count => {
      inflight.delete(url);
      if (typeof count === 'number') {
        cache.set(url, { count, at: Date.now() });
      }
      return count;
    },
    error => {
      inflight.delete(url);
      throw error;
    }
  );
  inflight.set(url, request);
  return request;
};

/**
 * One count through the shared cache — also used by useListingCounts.js (chips, zero ladder), so
 * the editor, the chips and the ladder never query the same URL twice within a minute.
 *
 * @param {Function} dispatch redux dispatch (thunk with the sdk)
 * @param {string} url target URL, the cache key
 * @param {Object} params SDK params from countParamsForUrl
 * @returns {Promise<number|undefined>}
 */
export const countWithCache = (dispatch, url, params) => fetchCount(dispatch, url, params);

/**
 * Counts for several target URLs at once.
 *
 * @param {string[]} urls path + query, as built by buildSearchTarget / spaceTypeHref
 * @param {Object} [options]
 * @param {boolean} [options.enabled] false → nothing is fetched and every count is undefined
 * @param {number} [options.delay] debounce in ms
 * @returns {Object} url → number | undefined (unknown, loading, failed or not countable)
 */
export const useListingCounts = (urls, { enabled = true, delay = COUNT_DEBOUNCE_MS } = {}) => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const groups = useSpaceTypeGroups();
  const [, setVersion] = useState(0);

  const key = (urls || []).filter(Boolean).join('\n');
  const entries = useMemo(
    () =>
      key
        ? key.split('\n').map(url => ({ url, params: countParamsForUrl(url, { groups, config }) }))
        : [],
    [key, groups, config]
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const pending = entries.filter(({ url, params }) => params && !isFresh(url));
    if (pending.length === 0) {
      return undefined;
    }
    let cancelled = false;
    const rerender = () => {
      if (!cancelled) {
        setVersion(v => v + 1);
      }
    };
    const timer = setTimeout(() => {
      pending.forEach(({ url, params }) => {
        fetchCount(dispatch, url, params).then(rerender, () => {
          // No number is better than a wrong one: the button falls back to "Suchen".
        });
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [entries, enabled, delay, dispatch]);

  const counts = {};
  entries.forEach(({ url, params }) => {
    counts[url] = enabled && params ? cachedCount(url) : undefined;
  });
  return counts;
};

/**
 * The count of one target URL.
 *
 * @param {string} url path + query
 * @param {Object} [options] see useListingCounts
 * @returns {number|undefined} undefined while unknown, loading, failed or not countable
 */
export const useListingCount = (url, options) => {
  const counts = useListingCounts(url ? [url] : [], options);
  return url ? counts[url] : undefined;
};

export default useListingCount;
